import { useCallback } from 'react';

import { ICartEntry, ICombo } from '@rbi-ctg/menu';
import {
  transformUserSelectionsComboToComboSlotSelections,
  transformUserSelectionsModifierToModifierSelection,
} from 'components/product-detail/utils';
import { useProductPrice } from 'hooks/menu/use-product-price';
import { mapEntryTypeToOfferType } from 'pages/cart/utils';
import { IAddToCartSelectionAttributes } from 'state/analytics/types';
import { useFavoritesContext } from 'state/favorites';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLocationContext } from 'state/location';
import { ILocation } from 'state/location/types';
import { LoyaltyAppliedOffer, isLoyaltyOffer } from 'state/loyalty/types';
import { isAppliedOfferSwap } from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useOffersContext } from 'state/offers';
import { useOrderContext } from 'state/order';
import { useProductWizardContext } from 'state/product-wizard';
import { useEditingCartEntry } from 'state/product-wizard/hooks/use-editing-cart-entry';
import { goToMenu, routes } from 'utils/routing';
import { IMenuObjectToCartEntry, transformMenuObjectToCartItem } from 'utils/wizard';

type LocationStateType = ILocation['location']['state'] & {
  isFromCart?: boolean;
  isFromSurprise?: boolean;
};

export const useProductCart = () => {
  const { selectFavorite, tempFavorite: currentFavorite } = useFavoritesContext();
  const { location, navigate } = useLocationContext();
  const {
    priceForItemOptionModifier,
    pricingFunction,
    priceForComboSlotSelection,
    isOffer,
  } = useMenuContext();
  const {
    selectedOffer: selectedLegacyOffer,
    setSelectedOfferCartEntry,
    setSelectedOfferPrice,
  } = useOffersContext();
  const { removeFromCart, upsertCart } = useOrderContext();
  const {
    selectedProduct,
    productQuantity,
    userSelections,
    manualModifications,
  } = useProductWizardContext();

  const dispatch = useAppDispatch();
  const editingCartEntry = useEditingCartEntry();
  const { totalPrice } = useProductPrice();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const selectedOffer = selectedLoyaltyOffer || selectedLegacyOffer;

  const { isFromCart, isFromSurprise } = (location.state as LocationStateType) || {};

  const handleOffers = useCallback(
    ({ mainEntry, upsellEntries, modifiersInjected }: IMenuObjectToCartEntry) => {
      const offerPrice = totalPrice(modifiersInjected);
      if (selectedOffer && isLoyaltyOffer(selectedOffer)) {
        if (!selectedOffer.isStackable) {
          appliedOffers.forEach(offer => {
            // remove offer from cart if it is not a swap or stackable offer
            if (!offer.isStackable && !isAppliedOfferSwap(offer)) {
              removeFromCart({ cartId: offer.cartId });
            }
          });
        }
        dispatch(
          actions.loyalty.applyOffer({
            id: selectedOffer.loyaltyEngineId,
            type: (selectedOffer as LoyaltyAppliedOffer).type,
            cartId: mainEntry?.cartId,
            isStackable: selectedOffer.isStackable,
            cmsId: selectedOffer._id,
          })
        );
        dispatch(actions.loyalty.setSelectedOffer(null));
        upsertCart([
          {
            ...mainEntry,
            price: offerPrice,
            offerVendorConfigs: selectedOffer.vendorConfigs,
            ...(mainEntry && { type: mapEntryTypeToOfferType(mainEntry.type) }),
          },
          ...upsellEntries,
        ]);
      } else {
        const offerEntry = mainEntry ? { ...mainEntry, sanityId: selectedOffer?._id ?? '' } : null;
        setSelectedOfferCartEntry(offerEntry);
        setSelectedOfferPrice(offerPrice);
      }
    },
    [
      appliedOffers,
      dispatch,
      removeFromCart,
      selectedOffer,
      setSelectedOfferCartEntry,
      setSelectedOfferPrice,
      totalPrice,
      upsertCart,
    ]
  );

  const addToOrder = useCallback(() => {
    const { mainEntry, upsellEntries, modifiersInjected } = transformMenuObjectToCartItem(
      selectedProduct,
      priceForItemOptionModifier,
      pricingFunction,
      priceForComboSlotSelection,
      {
        quantity: productQuantity,
        modifierSelections: transformUserSelectionsModifierToModifierSelection(userSelections),
        pickerSelections: userSelections.pickerAspects,
        comboSlotSelections: transformUserSelectionsComboToComboSlotSelections(
          userSelections.comboSlot,
          selectedProduct as ICombo
        ),
        location,
        // quickConfigs, // ignore for now - only TH uses quickConfigs
        existingCartId: editingCartEntry?.cartId,
      }
    );
    // Don't include the main entry if its an offer that gets set separately in offerCtx
    const cartEntries = isOffer ? upsellEntries : ([mainEntry, ...upsellEntries] as ICartEntry[]);

    // If we are currently editing a favorite  don't continue
    if (currentFavorite) {
      return selectFavorite({ cartEntries });
    }

    // Only update the cart if we actually have anything to add. (Offers will most likely not have anything here)
    if (cartEntries.length) {
      const selections: IAddToCartSelectionAttributes = {
        itemModified: manualModifications.modifiers,
        comboSlotSelection: manualModifications.comboSlot,
        pickerAspectSelection: manualModifications.pickerAspects,
      };
      upsertCart(cartEntries, selections);
    }

    if (isOffer && selectedOffer) {
      handleOffers({
        mainEntry,
        upsellEntries,
        modifiersInjected,
      });
    }

    // if editing and user coming from the cart or if is coming from the surprise modal
    if ((editingCartEntry && isFromCart) || isFromSurprise) {
      return navigate(routes.cart);
    }
    goToMenu({ navigate });
  }, [
    selectedProduct,
    priceForItemOptionModifier,
    pricingFunction,
    priceForComboSlotSelection,
    productQuantity,
    userSelections,
    location,
    editingCartEntry,
    isOffer,
    currentFavorite,
    selectedOffer,
    isFromCart,
    isFromSurprise,
    navigate,
    selectFavorite,
    manualModifications.modifiers,
    manualModifications.comboSlot,
    manualModifications.pickerAspects,
    upsertCart,
    handleOffers,
  ]);

  return {
    addToOrder,
  };
};
